import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Company ──────────────────────────────────────────────────────────────────

export interface Company {
  id: number;
  name: string;
  legalName: string | null;
  address: string | null;
  taxId: string | null;
  email: string | null;
  phone: string | null;
  logoPath: string | null;
  estimatePrefix: string;
  estimatePad: number;
  estimateNextSeq: number;
  invoicePrefix: string;
  invoicePad: number;
  invoiceNextSeq: number;
  paymentTerms: string | null;
  legalFooter: string | null;
  createdAt: number;
  updatedAt: number;
}

export function useCompany() {
  return useQuery<Company>({
    queryKey: ['billing-company'],
    queryFn: () => api.get<Company>('/api/billing/company'),
  });
}

// estimateNextSeq/invoiceNextSeq excluded — read-only, never sent by the
// client. Only assignEstimateNumber's atomic counter increment may move them.
export type UpdateCompanyInput = Partial<Omit<Company, 'id' | 'createdAt' | 'updatedAt' | 'estimateNextSeq' | 'invoiceNextSeq'>>;

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCompanyInput) => api.put<Company>('/api/billing/company', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-company'] }),
  });
}

// ── Accounts ─────────────────────────────────────────────────────────────────

export interface BillingAccount {
  id: number;
  companyId: number;
  name: string;
  currency: string;
  isActive: number;
  hasDocuments: boolean;
  createdAt: number;
  updatedAt: number;
}

export function useAccounts(includeInactive = false) {
  return useQuery<{ accounts: BillingAccount[] }>({
    queryKey: ['billing-accounts', includeInactive],
    queryFn: () => api.get<{ accounts: BillingAccount[] }>(`/api/billing/accounts?includeInactive=${includeInactive}`),
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { companyId: number; name: string; currency: string }) =>
      api.post<BillingAccount>('/api/billing/accounts', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-accounts'] }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; currency?: string; isActive?: boolean }) =>
      api.patch<BillingAccount>(`/api/billing/accounts/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-accounts'] }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: boolean }>(`/api/billing/accounts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-accounts'] }),
  });
}

// ── Estimates ────────────────────────────────────────────────────────────────

export interface EstimateLineItem {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export interface Estimate {
  id: number;
  accountId: number;
  contactId: number;
  projectId: number | null;
  status: 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'converted';
  slug: string | null;
  number: string | null;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  lineItemsJson: string;
  notes: string | null;
  validUntil: string | null;
  sentAt: number | null;
  respondedAt: number | null;
  acceptedAt: number | null;
  pdfPath: string | null;
  createdAt: number;
  updatedAt: number;
}

export function useEstimates(filters: { projectId?: number; contactId?: number } = {}) {
  const params = new URLSearchParams();
  if (filters.projectId !== undefined) params.set('projectId', String(filters.projectId));
  if (filters.contactId !== undefined) params.set('contactId', String(filters.contactId));
  const qs = params.toString();
  return useQuery<{ estimates: Estimate[] }>({
    queryKey: ['billing-estimates', filters],
    queryFn: () => api.get<{ estimates: Estimate[] }>(`/api/billing/estimates${qs ? `?${qs}` : ''}`),
  });
}

export function useEstimate(id: number | undefined) {
  return useQuery<Estimate>({
    queryKey: ['billing-estimate', id],
    queryFn: () => api.get<Estimate>(`/api/billing/estimates/${id}`),
    enabled: id !== undefined,
  });
}

export interface EstimateInput {
  accountId: number;
  contactId: number;
  projectId?: number | null;
  lineItems: Array<{ description: string; qty: number; unitPrice: number }>;
  taxRate?: number;
  notes?: string | null;
  validUntil?: string | null;
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EstimateInput) => api.post<Estimate>('/api/billing/estimates', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing-estimates'] }),
  });
}

// Mirrors useBulkDeleteReports (useReports.ts) exactly.
export function useBulkDeleteEstimates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete<{ deleted: number }>('/api/billing/estimates/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-estimates'] });
    },
  });
}

export function useUpdateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<EstimateInput>) =>
      api.patch<Estimate>(`/api/billing/estimates/${id}`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['billing-estimates'] });
      void qc.invalidateQueries({ queryKey: ['billing-estimate', vars.id] });
    },
  });
}

// ── Invoices ─────────────────────────────────────────────────────────────────
// Created via useConvertEstimate below. Editable while 'draft' via
// useUpdateInvoice — accountId/contactId/currency never change post-conversion.

export interface Invoice {
  id: number;
  accountId: number;
  contactId: number;
  projectId: number | null;
  estimateId: number | null;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  slug: string | null;
  number: string | null;
  currency: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  lineItemsJson: string;
  notes: string | null;
  dueDate: string | null;
  paymentTerms: string | null;
  legalFooter: string | null;
  sentAt: number | null;
  paidAt: number | null;
  paidMethod: string | null;
  pdfPath: string | null;
  createdAt: number;
  updatedAt: number;
}

export function useInvoices(filters: { projectId?: number; contactId?: number; estimateId?: number } = {}) {
  const params = new URLSearchParams();
  if (filters.projectId !== undefined) params.set('projectId', String(filters.projectId));
  if (filters.contactId !== undefined) params.set('contactId', String(filters.contactId));
  if (filters.estimateId !== undefined) params.set('estimateId', String(filters.estimateId));
  const qs = params.toString();
  return useQuery<{ invoices: Invoice[] }>({
    queryKey: ['billing-invoices', filters],
    queryFn: () => api.get<{ invoices: Invoice[] }>(`/api/billing/invoices${qs ? `?${qs}` : ''}`),
  });
}

export function useInvoice(id: number | undefined) {
  return useQuery<Invoice>({
    queryKey: ['billing-invoice', id],
    queryFn: () => api.get<Invoice>(`/api/billing/invoices/${id}`),
    enabled: id !== undefined,
  });
}

export interface InvoiceInput {
  lineItems?: Array<{ description: string; qty: number; unitPrice: number }>;
  taxRate?: number;
  notes?: string | null;
  dueDate?: string | null;
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & InvoiceInput) =>
      api.patch<Invoice>(`/api/billing/invoices/${id}`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
      void qc.invalidateQueries({ queryKey: ['billing-invoice', vars.id] });
    },
  });
}

// Mirrors useBulkDeleteEstimates exactly.
export function useBulkDeleteInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete<{ deleted: number }>('/api/billing/invoices/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
  });
}

// Converts an estimate into a new draft invoice — available on any
// non-converted estimate, regardless of sent/accepted status.
export function useConvertEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: number) => api.post<Invoice>(`/api/billing/estimates/${estimateId}/convert`, {}),
    onSuccess: (_invoice, estimateId) => {
      void qc.invalidateQueries({ queryKey: ['billing-estimates'] });
      void qc.invalidateQueries({ queryKey: ['billing-estimate', estimateId] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
  });
}

// Acción manual (Billing Phase 2) — una reply del cliente NUNCA flipea esto;
// el usuario la lee y marca aceptación acá. Solo estimates 'sent'.
export function useMarkEstimateAccepted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: number) => api.post<Estimate>(`/api/billing/estimates/${estimateId}/mark-accepted`, {}),
    onSuccess: (_estimate, estimateId) => {
      void qc.invalidateQueries({ queryKey: ['billing-estimates'] });
      void qc.invalidateQueries({ queryKey: ['billing-estimate', estimateId] });
    },
  });
}
